import { Controller } from "@hotwired/stimulus";

// Same fill-currentColor style as PSV's own navbar icons: a ringed planet
// for the little-planet toggle.
const LITTLE_PLANET_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
  '<circle cx="50" cy="50" r="24" fill="currentColor"/>' +
  '<ellipse cx="50" cy="50" rx="44" ry="13" fill="none" stroke="currentColor" stroke-width="6" transform="rotate(-20 50 50)"/>' +
  "</svg>";

// The gyroscope plugin's own compass icon (not exported), copied so our
// replacement button looks exactly like the stock one it stands in for.
const GYROSCOPE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
  '<path fill="currentColor" d="M50 0a50 50 0 1 0 0 100A50 50 0 0 0 50 0zm0 88.81a38.86 38.86 0 0 1-38.81-38.8 38.86 38.86 0 0 1 38.8-38.82A38.86 38.86 0 0 1 88.82 50 38.87 38.87 0 0 1 50 88.81z"/>' +
  '<path fill="currentColor" d="M72.07 25.9L40.25 41.06 27.92 74.12l31.82-15.18v-.01l12.32-33.03zM57.84 54.4L44.9 42.58l21.1-10.06-8.17 21.9z"/>' +
  "<!--Created by iconoci from the Noun Project--></svg>";

// The camera pose that reads as a tiny planet: looking straight down,
// fully zoomed out with a widened field of view and a strong fisheye
// offset (values from PSV's own intro-animation recipe). PSV removed its
// little-planet adapter in 5.8 — since then this pose on the regular
// equirectangular adapter IS the upstream way (the adapter's deprecation
// notice points at exactly this animation), and it keeps the texture and
// viewer alive through the toggle instead of re-instantiating.
const PLANET_VIEW = { pitch: -Math.PI / 2, zoom: 0, maxFov: 130, fisheye: 2 };
const PROJECTION_TRANSITION_MS = 1200;

// 360 viewer mount (#53): Photo Sphere Viewer v5 with its equirectangular
// adapter on the display blob — the metadata-stripped texture the pipeline
// capped at a WebGL-safe edge. One viewer instance per connect; disconnect
// destroys it so every Turbo navigation tears down the WebGL context and
// its listeners instead of leaking them across visits.
//
// PSV and its three.js dependency are dynamic imports on purpose: the
// controller registry pulls every controller into the app-wide entrypoint
// eagerly, and a static import here would ship the whole 360 payload to
// login, library and settings. This shell stays in the eager bundle; the
// heavy chunk loads only when a viewer actually mounts.
export default class extends Controller {
  static targets = ["screen"];
  static values = {
    panoramaUrl: String,
    lang: Object,
    // True inside a foreign iframe (the embed's fill mode): the viewer
    // must not swallow the host page's scrolling.
    embedded: Boolean,
  };

  async connect() {
    // The token pairs each viewer creation with the connect that started
    // it: a Turbo navigation during the awaits below must not resurrect a
    // viewer into a detached element.
    const token = {};
    this.connectToken = token;
    const [{ Viewer, utils }, , { GyroscopePlugin }] = await Promise.all([
      import("@photo-sphere-viewer/core"),
      import("@photo-sphere-viewer/core/index.css"),
      import("@photo-sphere-viewer/gyroscope-plugin"),
    ]);
    if (this.connectToken !== token) return;
    this.utils = utils;

    // No WebGL 2, no viewer — answer honestly instead of PSV's error
    // overlay across a blurred thumb (!37 review: share pages no longer
    // sit behind the modern-browser gate, so a WebGL-less visitor
    // reaches this mount). This is PSV's OWN support check, asked at
    // PSV's own moment: SYSTEM.load() decides "supported" with exactly
    // this call, so the fallback fires precisely when PSV would show
    // its overlay. The timing is load-bearing — the same probe at
    // connect time (before the import round-trip) answers null on CI's
    // headless Firefox while the viewer there works.
    if (!document.createElement("canvas").getContext("webgl2")) {
      this.showFlatFallback();
      return;
    }

    // A Turbo cache restore re-connects on a snapshot that still contains
    // the previous visit's viewer DOM — clear the mount so the viewer
    // never doubles up.
    this.screenTarget.replaceChildren();
    // Projection state lives per mount (#54: per page visit, no persist);
    // the attribute is also the DOM contract the E2E toggle assertions
    // read.
    this.element.dataset.projection = "sphere";
    this.viewer = this.buildViewer(Viewer, GyroscopePlugin);

    // Start the gyroscope button hidden — but NOT via the config's
    // visible flag: CustomButton's constructor hide() refreshes the
    // navbar mid-setButtons, before PSV builds its menu button, and when
    // the stock buttons overflow the stage (~280px and below) that is a
    // TypeError inside new Viewer() taking the whole mount down. Hiding
    // here is the same pre-paint moment without the landmine.
    this.viewer.navbar.getButton("gyroscope").hide();

    // Gyroscope and little planet both want the camera: when the gyroscope
    // takes over, fold the planet back into the sphere. The same event
    // mirrors the button state — start() and stop() are called from more
    // places than the button (fold-back below, permission flow), so the
    // event is the one truthful source.
    this.gyroscope = this.viewer.getPlugin(GyroscopePlugin);
    this.gyroscope.addEventListener("gyroscope-updated", (event) => {
      this.setTogglePressed("gyroscope", event.gyroscopeEnabled);
      if (event.gyroscopeEnabled && this.planetActive())
        this.toggleProjection();
    });
    // The stock button's visibility contract, replicated: hidden until
    // the plugin's support probe answers true. On iOS that answer only
    // means requestPermission exists; the prompt itself must wait for
    // the button's click gesture. The probe can outlive this mount (its
    // desktop path waits up to 10s for a sensor event), hence the token.
    this.gyroscope.isSupported().then((supported) => {
      if (supported && this.connectToken === token)
        this.viewer.navbar.getButton("gyroscope").show();
    });

    // PSV navbar buttons are divs: toggleActive only paints a CSS class,
    // and there is no role or pressed state for assistive tech. All
    // three stateful toggles get the role and pressed state named here.
    // Naming role="button" also claims the button contract's keyboard
    // half: PSV's own keydown answers Enter only, so Space (which would
    // otherwise scroll the page) is wired here.
    for (const [id, action] of [
      ["little-planet", () => this.toggleProjection()],
      ["gyroscope", () => this.toggleGyroscope()],
      ["fullscreen", () => this.viewer.toggleFullscreen()],
    ]) {
      const { container } = this.viewer.navbar.getButton(id);
      container.setAttribute("role", "button");
      container.setAttribute("aria-pressed", "false");
      container.addEventListener("keydown", (event) => {
        if (event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        action();
      });
    }

    // The kuppel toggles mirror both halves via setTogglePressed; the
    // stock fullscreen button paints its own active style, so only the
    // aria half needs mirroring — from the viewer's event, not the
    // click: Escape and the browser chrome leave fullscreen too.
    this.viewer.addEventListener("fullscreen", (event) => {
      this.viewer.navbar
        .getButton("fullscreen")
        .container.setAttribute(
          "aria-pressed",
          String(event.fullscreenEnabled),
        );
    });

    // The denial notification must also be HEARD: PSV's notification
    // container carries no role, and inside the stage's role="img" plain
    // text may never reach assistive tech — role="status" (implicit
    // polite live region) announces whatever it shows.
    this.viewer.notification.container.setAttribute("role", "status");
  }

  buildViewer(Viewer, GyroscopePlugin) {
    return new Viewer({
      container: this.screenTarget,
      panorama: this.panoramaUrlValue,
      // The #53 control set plus #54's projection + gyroscope toggles. No
      // download button (the download menu arrives with M9) and no
      // caption/description — the page around the viewer owns the title.
      navbar: [
        "zoom",
        "move",
        {
          id: "little-planet",
          // Resolved through the viewer lang config (localized Ruby-side).
          title: "littlePlanet",
          className: "kuppel-little-planet-button",
          content: LITTLE_PLANET_ICON,
          onClick: () => this.toggleProjection(),
        },
        {
          // Our own button in place of the plugin's stock "gyroscope":
          // the stock button drops start()'s rejection, and on iOS the
          // support probe only proves the permission API exists — so
          // after a denied motion prompt (the denial sticks for the
          // page) it would sit live-looking and silently dead on every
          // further tap. Same icon, same lang key, same feature-detected
          // visibility (wired below), but the denial answers with a
          // notification (toggleGyroscope).
          id: "gyroscope",
          title: "gyroscope",
          className: "kuppel-gyroscope-button",
          content: GYROSCOPE_ICON,
          onClick: () => this.toggleGyroscope(),
        },
        "fullscreen",
      ],
      // PSV's own default, explicit because it is a decision: keyboard nav
      // lives in fullscreen. "always" would bind arrow/page keys on window
      // for the whole document — fighting page scroll today and M6's
      // inline editing later.
      keyboard: "fullscreen",
      // Transparent canvas: the blurred placeholder behind it shows
      // through while the texture streams in (progressive loading).
      canvasBackground: "rgba(0, 0, 0, 0)",
      // Embedded (a foreign iframe): plain wheel and one-finger drag
      // belong to the HOST page's scrolling — PSV registers non-passive
      // listeners that would swallow it (the canonical maps-embed
      // complaint), and ships this option pair for exactly that
      // context. The ctrlZoom/twoFingers overlay strings have been in
      // the lang config since #53; this is the switch that makes their
      // code path reachable.
      mousewheelCtrlKey: this.embeddedValue,
      touchmoveTwoFingers: this.embeddedValue,
      lang: this.langValue,
      plugins: [GyroscopePlugin],
    });
  }

  // The honest no-WebGL answer: the blurred thumb behind the canvas is
  // already the right picture, so un-blur it and say why it stays flat.
  // (The thumb is a flat centre crop of the equirect — a stretched
  // slice, not a framed view; #106 is what would make it good.) The
  // attribute is the DOM contract the E2E fallback assertion reads.
  showFlatFallback() {
    // A Turbo cache restore re-connects on a snapshot that already
    // carries the note — the attribute doubles as the idempotency guard.
    if (this.element.dataset.viewerFallback) return;

    this.element.dataset.viewerFallback = "webgl";
    this.element.querySelector("img")?.classList.remove("blur-md", "scale-105");
    const note = document.createElement("p");
    note.textContent = this.langValue.webglFallback;
    note.className =
      "absolute inset-x-0 bottom-0 bg-background/80 px-4 py-2 text-center text-sm";
    this.element.appendChild(note);
  }

  disconnect() {
    this.connectToken = null;
    this.projectionAnimation?.cancel();
    this.projectionAnimation = null;
    // destroy() tears down plugins and navbar with the viewer, so the
    // gyroscope listeners above die here too — except the support
    // probe's window listener (upstream), which outlives destroy() until
    // its first orientation event or 10s timeout. Self-healing, so left
    // to upstream.
    this.viewer?.destroy();
    this.viewer = null;
    this.gyroscope = null;
  }

  // #54's product moment: one button morphs the sphere into a movable tiny
  // planet and back. One utils.Animation drives pitch, zoom and fisheye
  // together on the LIVE viewer (see PLANET_VIEW above); yaw stays free the
  // whole time, so the planet keeps spinning under the user's finger even
  // mid-transition.
  toggleProjection() {
    const entering = !this.planetActive();
    this.element.dataset.projection = entering ? "planet" : "sphere";
    this.setTogglePressed("little-planet", entering);

    const from = {
      pitch: this.viewer.getPosition().pitch,
      zoom: this.viewer.getZoomLevel(),
      maxFov: this.viewer.config.maxFov,
      fisheye: this.viewer.config.fisheye,
    };
    let target;
    if (entering) {
      this.gyroscope.stop();
      // Where the sphere returns to when the planet folds back — captured
      // only from a SETTLED sphere: re-entering while the fold-back still
      // animates reads mid-flight camera values, and freezing those as
      // the restore pose would tilt the base view a little more on every
      // rapid double-tap.
      if (!this.projectionAnimation) {
        this.sphereView = {
          pitch: from.pitch,
          zoom: from.zoom,
          maxFov: from.maxFov,
        };
      }
      target = PLANET_VIEW;
    } else {
      target = { ...this.sphereView, fisheye: 0 };
    }

    this.projectionAnimation?.cancel();
    const animation = new this.utils.Animation({
      properties: {
        pitch: { start: from.pitch, end: target.pitch },
        zoom: { start: from.zoom, end: target.zoom },
        maxFov: { start: from.maxFov, end: target.maxFov },
        fisheye: { start: from.fisheye, end: target.fisheye },
      },
      duration: PROJECTION_TRANSITION_MS,
      easing: "inOutQuad",
      onTick: (properties) => {
        // maxFov only re-derives the zoom level; the zoom() call below
        // applies the widened fov each tick.
        this.viewer.setOptions({
          fisheye: properties.fisheye,
          maxFov: properties.maxFov,
        });
        // While the gyroscope owns the orientation (leaving planet mode
        // because it started), animating only fisheye + zoom avoids
        // fighting it over the camera.
        if (!this.gyroscope.isEnabled()) {
          this.viewer.rotate({
            yaw: this.viewer.getPosition().yaw,
            pitch: properties.pitch,
          });
        }
        this.viewer.zoom(properties.zoom);
      },
    });
    this.projectionAnimation = animation;
    // Resolves true on completion (false on cancel, where the reference
    // was already replaced): a null reference is the "settled" signal the
    // capture above reads.
    animation.then(() => {
      if (this.projectionAnimation === animation)
        this.projectionAnimation = null;
    });
  }

  // Why not plugin.toggle(): it discards start()'s promise, which is
  // exactly the stock button's flaw. start() rejects when the motion
  // prompt is denied — and iOS keeps that denial for the page, so every
  // further tap fails the same way. Each one answers with the
  // notification instead of silence. start() has a second reject path
  // (unsupported, indistinguishable upstream — it rejects with no
  // argument); a rejection HERE is still always the denial because the
  // button only shows after the support probe answered true (the show()
  // wiring in connect) — change that visibility logic and this message
  // can start to lie.
  toggleGyroscope() {
    if (this.gyroscope.isEnabled()) {
      this.gyroscope.stop();
    } else {
      this.gyroscope.start().catch(() => {
        // The permission prompt can outlive a Turbo navigation.
        this.viewer?.notification.show({
          content: this.langValue.gyroscopeDenied,
          timeout: 5000,
        });
      });
    }
  }

  // toggleActive paints PSV's active style; aria-pressed tells assistive
  // tech the same thing.
  setTogglePressed(id, active) {
    const button = this.viewer.navbar.getButton(id);
    button.toggleActive(active);
    button.container.setAttribute("aria-pressed", String(active));
  }

  planetActive() {
    return this.element.dataset.projection === "planet";
  }
}
